import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_DASHBOARD: Record<string, string> = {
  admin: '/dashboard/admin',
  restaurateur: '/dashboard/restaurateur',
  commercant: '/dashboard/commercant',
  livreur: '/dashboard/livreur',
  client: '/dashboard/utilisateur',
};

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { user, userRole, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isHomePage = location.pathname === '/';
  const isTransparent = isHomePage && !isScrolled;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setIsProfileOpen(false);
    navigate('/');
  };

  const dashboardPath = userRole ? ROLE_DASHBOARD[userRole] ?? '/profil' : '/profil';
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Mon profil';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isTransparent ? 'bg-transparent' : 'bg-white shadow-md'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src="https://public.readdy.ai/ai/img_res/b05e588d-5161-4e8a-9fc5-c87d2449f69a.png"
              alt="Localygo"
              className="h-24 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <Link to="/restaurants" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isTransparent ? 'text-white hover:text-teal-200' : 'text-gray-700 hover:text-teal-600'
            }`}>
              Restaurants
            </Link>
            <Link to="/boutiques" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isTransparent ? 'text-white hover:text-teal-200' : 'text-gray-700 hover:text-teal-600'
            }`}>
              Boutiques
            </Link>
            <Link to="/commander" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isTransparent ? 'text-white hover:text-teal-200' : 'text-gray-700 hover:text-teal-600'
            }`}>
              Commander
            </Link>
            <Link to="/livreur" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isTransparent ? 'text-white hover:text-teal-200' : 'text-gray-700 hover:text-teal-600'
            }`}>
              Devenir livreur
            </Link>
            <Link to="/partenaire" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              isTransparent ? 'text-white hover:text-teal-200' : 'text-gray-700 hover:text-teal-600'
            }`}>
              Devenir partenaire
            </Link>

            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isTransparent
                      ? 'bg-white/15 text-white hover:bg-white/25'
                      : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                  }`}
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-teal-600 text-white text-xs font-bold">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                  <span className="max-w-[120px] truncate">{displayName}</span>
                  <i className={`ri-arrow-down-s-line text-base w-4 h-4 flex items-center justify-center transition-transform ${isProfileOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
                    <Link
                      to="/profil"
                      onClick={() => setIsProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 cursor-pointer transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-user-line"></i></span>
                      Mon profil
                    </Link>
                    <Link
                      to={dashboardPath}
                      onClick={() => setIsProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 cursor-pointer transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-dashboard-line"></i></span>
                      Mon tableau de bord
                    </Link>
                    <Link
                      to="/suivi-commande"
                      onClick={() => setIsProfileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 cursor-pointer transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
                      Suivi de commande
                    </Link>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                    >
                      <span className="w-5 h-5 flex items-center justify-center"><i className="ri-logout-box-line"></i></span>
                      Se déconnecter
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to="/connexion" 
                className="bg-teal-600 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Connexion
              </Link>
            )}
          </div>

          {/* Mobile Menu - Right Side */}
          <div className="md:hidden flex items-center gap-2">
            {user ? (
              <Link
                to="/profil"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  isTransparent
                    ? 'border-white/80 text-white hover:bg-white/20'
                    : 'border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white'
                }`}
              >
                <i className="ri-user-line text-sm w-4 h-4 flex items-center justify-center"></i>
                Mon profil
              </Link>
            ) : (
              <Link 
                to="/connexion" 
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  isTransparent
                    ? 'border-white/80 text-white hover:bg-white/20'
                    : 'border-teal-600 text-teal-600 hover:bg-teal-600 hover:text-white'
                }`}
              >
                <i className="ri-user-line text-sm w-4 h-4 flex items-center justify-center"></i>
                Connexion
              </Link>
            )}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-9 h-9 flex items-center justify-center cursor-pointer rounded-lg transition-colors ${
                isTransparent ? 'text-white hover:bg-white/10' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg">
          <div className="px-4 py-4 space-y-3">
            <Link to="/restaurants" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">
              Restaurants
            </Link>
            <Link to="/boutiques" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">
              Boutiques
            </Link>
            <Link to="/commander" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">
              Commander
            </Link>
            <Link to="/livreur" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">
              Devenir livreur
            </Link>
            <Link to="/partenaire" onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">
              Devenir partenaire
            </Link>
            {user && (
              <>
                <div className="border-t border-gray-100 pt-3">
                  <Link to={dashboardPath} onClick={() => setIsMobileMenuOpen(false)} className="block text-gray-700 hover:text-teal-600 font-medium cursor-pointer">
                    Mon tableau de bord
                  </Link>
                </div>
                <button
                  onClick={() => { handleSignOut(); setIsMobileMenuOpen(false); }}
                  className="block w-full text-left text-red-500 hover:text-red-600 font-medium cursor-pointer"
                >
                  Se déconnecter
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
